/**
 * +specs: every class's specialisations (docs/design/classes/specs.md). One file per class; this
 * index only concatenates them, so the class agents never edit the same file.
 */
import type { AbilityDef, ClassDef, SpecDef } from '@keystone/sim';
import { applySpec, specClassId } from '@keystone/sim';
import { ACOLYTE_SPECS, ACOLYTE_SPEC_INFO } from './acolyte';
import { MAGE_SPECS, MAGE_SPEC_INFO } from './mage';
import { RANGER_SPECS, RANGER_SPEC_INFO } from './ranger';
import { ROGUE_SPECS, ROGUE_SPEC_INFO } from './rogue';
import type { SpecInfo } from './types';
import { WARRIOR_SPECS, WARRIOR_SPEC_INFO } from './warrior';

export type { SpecInfo } from './types';
export * from './warrior';
export * from './rogue'; // +rogue-specs
export { MAGE_SPECS, MAGE_SPEC_INFO } from './mage';
export { ACOLYTE_SPECS, ACOLYTE_SPEC_INFO } from './acolyte';
export { RANGER_SPECS, RANGER_SPEC_INFO } from './ranger';

/** Every implemented specialisation (content bundle `specs`). */
export const SPECS: SpecDef[] = [...WARRIOR_SPECS, ...ROGUE_SPECS, ...MAGE_SPECS, ...ACOLYTE_SPECS, ...RANGER_SPECS];

/** All 15 specs' preview info, by base class (implemented or not), in advancement.md order. */
export const SPEC_INFO: Readonly<Record<string, readonly SpecInfo[]>> = {
  warrior: WARRIOR_SPEC_INFO,
  rogue: ROGUE_SPEC_INFO,
  mage: MAGE_SPEC_INFO,
  acolyte: ACOLYTE_SPEC_INFO,
  ranger: RANGER_SPEC_INFO,
};

/** Level the Tower Warden offers specialisations at. */
export const SPEC_LEVEL = 10;

/** An implemented spec by id. */
export const specDef = (id: string | null | undefined): SpecDef | undefined => (id ? SPECS.find((s) => s.id === id) : undefined);

/** Implemented specs of a base class. */
export const specsOf = (baseClassId: string): SpecDef[] => SPECS.filter((s) => s.baseClassId === baseClassId);

/** The spec's preview info (implemented or not). */
export const specInfo = (id: string | null | undefined): SpecInfo | undefined => (id ? Object.values(SPEC_INFO).flat().find((s) => s.id === id) : undefined);

/**
 * The base class with a spec folded in (`applySpec`); `keyed` registers it beside the base under
 * "warrior@ironclad" (Group Finder strangers, the harness), else it replaces the base id (the hero).
 */
export function specClass(base: ClassDef, specId: string, keyed = false): ClassDef {
  const s = specDef(specId);
  if (!s) throw new Error(`unknown spec ${specId}`);
  return applySpec(base, s, keyed ? { id: specClassId(base.id, s.id) } : {});
}

/** Every spec ability (VFX / icon aliasing, the name rule test). */
export const SPEC_ABILITIES: AbilityDef[] = SPECS.flatMap((s) => s.abilities);
